import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { matchIsValidTel, MuiTelInput } from "mui-tel-input";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Box,
  Stack,
  Button,
} from "@mui/material";

import logo from "../../assets/images/admin-icon-2/Logo.png";
import playerdex from "../../assets/images/admin-icon-2/playerdex.png";
import manager from "../../assets/images/admin-icon-2/manager.png";
import {
  appVersion,
  completeSignInWithEmailLink,
  getStorage,
  sendSignInLink,
  signIn,
  verificationCode,
} from "../../auth/utils";
import { GET_USER } from "../../graphql/userGraphql";
import { AuthContext } from "../../context/AuthContext";
import backgroundImage from "../../assets/images/admin-icon-2/bg-black.png";
import { SnackBarContext } from "../../context/SnackBarContext";
import { useTranslation } from "../../context/Internationalization";
import TranslationToggle from "../../Components/TranslationToggle";
import useFormStyles from "../../Components/Form/formStyles";
import CheckedIcon from "../../assets/images/icons/_Checkbox base.svg";
import UnCheckedIcon from "../../assets/images/icons/_UnCheckbox base.svg";
import { styled } from "@mui/material/styles";

import { makeStyles } from "@mui/styles";
import { colors } from "../../theme/colors";
import { fonts } from "../../theme/fonts";
import useGraphql from "../../Hooks/useGraphql";
import useLog from "../../Hooks/useLog";
import { USER_ROLES } from "../../utility/Enum";
import { isClubAdmin, isSuperAdmin } from "../../utility/Utils";

const { Grey } = colors;
const { text } = fonts;
const MAGIC_LINK_SUCCESS_REDIRECT_DELAY_MS = 700;

const useStyles = makeStyles({
  descriptionText: {
    ...text.md.normal,
    color: Grey[500],
    textAlign: "center",
    marginBottom: "24px",
  },
  buttonText: {
    ...text.md.normal,
    color: Grey[800],
  },
  divider: {
    "&::before": {
      borderTop: "thin solid #7E8CA7 !important",
    },
    "&::after": {
      borderTop: "thin solid #7E8CA7 !important",
    },
  },
});

const BpIcon = styled("span")(({ theme }) => ({
  borderRadius: 6,
  width: 20,
  height: 20,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 0 0 1px rgb(16 22 26 / 40%)"
      : "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
  backgroundColor: theme.palette.mode === "dark" ? "#394b59" : "#f5f8fa",
  backgroundImage: `url("${UnCheckedIcon}")`,
  ".Mui-focusVisible &": {
    outline: "2px auto rgba(19,124,189,.6)",
    outlineOffset: 2,
  },
  "input:hover ~ &": {
    backgroundColor: theme.palette.mode === "dark" ? "#30404d" : "#ebf1f5",
  },
  "input:disabled ~ &": {
    boxShadow: "none",
    background:
      theme.palette.mode === "dark"
        ? "rgba(57,75,89,.5)"
        : "rgba(206,217,224,.5)",
  },
}));

const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: "#137cbd",
  backgroundImage:
    "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
  "&:before": {
    display: "block",
    width: 20,
    height: 20,
    backgroundImage: `url("${CheckedIcon}")`,
    content: '""',
  },
  "input:hover ~ &": {
    backgroundColor: "#106ba3",
  },
});

const getInitialRememberMe = () => localStorage.getItem("rememberMe");

const initialState = {
  mobile: "",
  email: "",
  loginMethod: "phone",
  code: "",
  confirmationResult: false,
  invalidRole: false,
  rememberMe: getInitialRememberMe(),
  phoneRememberMe: getInitialRememberMe(),
  status: "idle",
};

export const Login = () => {
  const [form, setForm] = useState(initialState);
  const { lang, t } = useTranslation();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const snackBar = useContext(SnackBarContext);
  const styles = useStyles();
  const formStyles = useFormStyles();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();
  const [isValidPhone, setIsValidPhone] = useState(true);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = !form.email || emailRegex.test(form.email.trim());

  const fetchUserAndCompleteLogin = (
    token,
    source,
    loginMethod,
    options = {}
  ) => {
    return axiosGraphQL({ query: GET_USER })
      .then((response) => {
        if (response.data.data.me.club && response.data.data.me.user) {
          const user = response.data.data.me.user;
          user.club = response.data.data.me.club;
          auth.setAuthState({ user, token });
          getStorage().setItem("user", JSON.stringify(user));
          if (loginMethod === "sms" && !user.email) {
            getStorage().setItem("lastLoginMethod", "sms");
          } else if (loginMethod === "email") {
            getStorage().setItem("lastLoginMethod", "email");
          }
          const areValidRoles = user.roles.some(
            (role) =>
              role === USER_ROLES.CLUB_ADMIN || role === USER_ROLES.SUPER_ADMIN
          );
          if (areValidRoles) {
            const nextStatus = options.showSuccess ? "success" : "resolve";
            setForm((prevState) => ({
              ...prevState,
              status: nextStatus,
              invalidRole: false,
            }));

            if (options.showSuccess) {
              snackBar.setOptions({
                alertMessage: t("login_success"),
                alertSeverity: "success",
              });
              snackBar.setSnackbarOpen(true);
              setTimeout(() => {
                navigate("/activities", { replace: true });
              }, MAGIC_LINK_SUCCESS_REDIRECT_DELAY_MS);
            } else {
              navigate("/activities", { replace: true });
            }
          } else {
            getStorage().removeItem("token");
            getStorage().removeItem("tokenLastValidity");
            setForm((prevState) => ({
              ...prevState,
              status: "rejected",
              invalidRole: true,
            }));
          }
        } else {
          throw new Error("error_during_login");
        }
      })
      .catch((error) => {
        errorLog(error, "Login", source, "GET_USER");
        getStorage().removeItem("token");
        getStorage().removeItem("tokenLastValidity");
        snackBar.setOptions({
          alertMessage: t("error_during_login"),
          alertSeverity: "error",
        });
        snackBar.setSnackbarOpen(true);
        setForm((prevState) => ({ ...prevState, status: "rejected" }));
      });
  };

  useEffect(() => {
    // Check if the update modal should be shown
    if (localStorage.getItem("showVersionUpdateModal") === "true") {
      snackBar.setOptions({
        alertMessage: t("app_updated_message"),
        alertSeverity: "info",
      });
      snackBar.setSnackbarOpen(true);

      // Remove flag after displaying the message
      localStorage.removeItem("showVersionUpdateModal");
    }
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const isMagicLink =
      searchParams.get("mode") === "signIn" && searchParams.get("oobCode");
    const loginError = searchParams.get("error");

    if (loginError) {
      snackBar.setOptions({
        alertMessage: t(loginError),
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
      window.history.replaceState(null, "", "/login");
      return;
    }

    if (!isMagicLink) {
      return;
    }

    localStorage.setItem("rememberMe", 1);
    setForm((prevState) => ({
      ...prevState,
      loginMethod: "email",
      rememberMe: 1,
      status: "pending",
    }));

    completeSignInWithEmailLink()
      .then(({ idToken }) => {
        window.history.replaceState(null, "", "/login");

        return fetchUserAndCompleteLogin(idToken, "emailLink", "email", {
          showSuccess: true,
        });
      })
      .catch((error) => {
        errorLog(error, "Login", "emailLink", "completeSignInWithEmailLink");
        getStorage().removeItem("token");
        getStorage().removeItem("tokenLastValidity");
        const messageKey =
          error.message === "login_session_expired" ||
          error.message === "invalid_or_expired_login_link"
            ? error.message
            : "invalid_or_expired_login_link";
        snackBar.setOptions({
          alertMessage: t(messageKey),
          alertSeverity: "error",
        });
        snackBar.setSnackbarOpen(true);
        window.history.replaceState(null, "", "/login");
        setForm((prevState) => ({ ...prevState, status: "rejected" }));
      });
  }, [navigate, auth]);

  useEffect(() => {
    const token = getStorage().getItem("token");
    if (token) {
      axiosGraphQL({ query: GET_USER })
        .then((response) => {
          const user = response.data.data.me?.user;
          if (!user) {
            auth.logout();
            return;
          }

          const isValidRole =
            user.roles.includes(USER_ROLES.CLUB_ADMIN) ||
            user.roles.includes(USER_ROLES.SUPER_ADMIN);

          if (isValidRole) {
            auth.setAuthState({ user, token });
            setForm((prevState) => ({
              ...prevState,
              invalidRole: false,
              status: "resolve",
            }));
            navigate("/activities", { replace: true });
          } else {
            setForm((prevState) => ({
              ...prevState,
              status: "rejected",
              invalidRole: true,
            }));
          }
        })
        .catch(() => {
          auth.logout();
        });
    } else {
      if (
        !localStorage.getItem("appVersion") ||
        localStorage.getItem("appVersion") !== appVersion
      ) {
        localStorage.setItem("appVersion", appVersion);
        snackBar.setOptions({
          alertMessage: t("app_updated_message"),
          alertSeverity: "info",
        });
        snackBar.setSnackbarOpen(true);
      }
    }
  }, [navigate, auth]);

  const updateField = ({ name, value }) => {
    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleOnChange = (value) => {
    const isValidTel = matchIsValidTel(value, {
      onlyCountries: ["GR", "CY"],
    });
    setIsValidPhone(isValidTel);

    setForm({
      ...form,
      mobile: value?.replace(/ /g, ""),
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!form.confirmationResult) {
      setForm({ ...form, status: "pending" });
      signIn(`${form.mobile?.trim()}`)
        .then((result) => {
          setForm({
            ...form,
            confirmationResult: result,
            status: "resolve",
            code: "",
          });
        })
        .catch((error) => {
          errorLog(error, "Login", "handleSubmit", "signIn");
          let errorMessage = t("error_during_login");
          if (
            error.response?.data?.error?.message ===
            "TOO_MANY_ATTEMPTS_TRY_LATER"
          ) {
            errorMessage = t("too_many_attempts_try_later"); // Provide a better user-friendly message
          }
          snackBar.setOptions({
            alertMessage: errorMessage,
            alertSeverity: "error",
          });
          snackBar.setSnackbarOpen(true);
          setTimeout(() => {
            window.location.reload();
          }, 2000);
          setForm({ ...initialState });
        });
    } else {
      setForm({
        ...form,
        status: "pending",
      });
      verificationCode(form.confirmationResult, form.code)
        .then((token) => {
          getStorage().setItem("token", token);
          getStorage().setItem("tokenLastValidity", Date.now().toString());

          fetchUserAndCompleteLogin(token, "handleSubmit", "sms");
        })
        .catch((error) => {
          errorLog(error, "Login", "handleSubmit", "verificationCode");
          let errorMessage = t("error_during_login");
          if (
            error.response?.data?.error?.message ===
            "TOO_MANY_ATTEMPTS_TRY_LATER"
          ) {
            errorMessage = t("too_many_attempts_try_later"); // Provide a better user-friendly message
          }
          snackBar.setOptions({
            alertMessage: errorMessage,
            alertSeverity: "error",
          });
          snackBar.setSnackbarOpen(true);
          setTimeout(() => {
            window.location.reload();
          }, 2000);
          setForm({ ...initialState });
        });
    }
  };

  const handleEmailSubmit = (event) => {
    event.preventDefault();
    const normalizedEmail = form.email.trim().toLowerCase();

    if (!emailRegex.test(normalizedEmail)) {
      snackBar.setOptions({
        alertMessage: t("invalid_email"),
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
      return;
    }

    localStorage.setItem("rememberMe", 1);
    setForm({
      ...form,
      email: normalizedEmail,
      rememberMe: 1,
      status: "pending",
    });
    sendSignInLink(normalizedEmail, lang)
      .then(() => {
        snackBar.setOptions({
          alertMessage: t("email_link_sent"),
          alertSeverity: "success",
        });
        snackBar.setSnackbarOpen(true);
        setForm((prevState) => ({ ...prevState, status: "resolve" }));
      })
      .catch((error) => {
        errorLog(error, "Login", "handleEmailSubmit", "sendSignInLink");
        snackBar.setOptions({
          alertMessage: t(error.message) || t("error_during_login"),
          alertSeverity: "error",
        });
        snackBar.setSnackbarOpen(true);
        setForm((prevState) => ({ ...prevState, status: "rejected" }));
      });
  };

  const handleRememberMe = ({ target: { checked } }) => {
    if (form.loginMethod === "email") {
      return;
    }

    if (checked) {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
      localStorage.setItem("rememberMe", 1);
      setForm({ ...form, rememberMe: 1, phoneRememberMe: 1 });
    } else {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("rememberMe");
      setForm({ ...form, rememberMe: 0, phoneRememberMe: 0 });
    }
  };

  return (
    <Box
      component="div"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        background: "#000",
        backgroundImage: `url(${backgroundImage})`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        width: "100vw",
        height: "100vh",
      }}
    >
      <Box component="div" sx={{ width: "400px" }}>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <Box>
            <img style={{ width: "80px" }} src={logo} alt="PlayerDex logo" />
          </Box>
          <Box>
            <img style={{ width: "80px" }} src={playerdex} alt="playerdex" />
          </Box>
          <Box>
            <img style={{ width: "80px" }} src={manager} alt="manager" />
          </Box>
        </Box>

        {form.invalidRole && (
          <div className={styles.descriptionText}>
            {t("login_through_mobile_app")}
          </div>
        )}

        {form.invalidRole && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            <Box
              sx={{
                marginRight: "8px",
              }}
            >
              <a
                href="http://onelink.to/6t2n3y"
                target="_blank"
                className="w-inline-block"
              >
                <img
                  src="https://uploads-ssl.webflow.com/6107c88a48c6b14e65e7d498/610dbf2e792761dea9e44ead_App%20Store%20Download.svg"
                  loading="lazy"
                  alt="App Store"
                />
              </a>
            </Box>
            <Box>
              <a
                href="http://onelink.to/6t2n3y"
                target="_blank"
                className="w-inline-block"
              >
                <img
                  src="https://uploads-ssl.webflow.com/6107c88a48c6b14e65e7d498/610dbf2eb54ed23d435906dc_Google%20Play%20Download.svg"
                  loading="lazy"
                  alt="Google Play"
                />
              </a>
            </Box>
          </Box>
        )}

        {!form.invalidRole && (
          <div className={styles.descriptionText}>
            {form.status === "success"
              ? t("login_success")
              : form.loginMethod === "phone"
              ? t("enter_phone_msg")
              : t("check_email")}
          </div>
        )}
        {!form.invalidRole && (
          <>
            <Stack direction="row" spacing={1} sx={{ marginBottom: "16px" }}>
              <Button
                fullWidth
                variant={
                  form.loginMethod === "phone" ? "contained" : "outlined"
                }
                onClick={() => {
                  setForm((prevState) => {
                    if (prevState.phoneRememberMe) {
                      localStorage.setItem("rememberMe", 1);
                    } else {
                      localStorage.removeItem("rememberMe");
                    }

                    return {
                      ...prevState,
                      loginMethod: "phone",
                      rememberMe: prevState.phoneRememberMe,
                    };
                  });
                }}
              >
                <span className={styles.buttonText}>
                  {t("login_with_phone")}
                </span>
              </Button>
              <Button
                fullWidth
                variant={
                  form.loginMethod === "email" ? "contained" : "outlined"
                }
                onClick={() => {
                  localStorage.setItem("rememberMe", 1);
                  setForm((prevState) => ({
                    ...prevState,
                    loginMethod: "email",
                    rememberMe: 1,
                    phoneRememberMe: prevState.rememberMe,
                    confirmationResult: false,
                  }));
                }}
              >
                <span className={styles.buttonText}>
                  {t("login_with_email")}
                </span>
              </Button>
            </Stack>
            <form
              className="login-form"
              onSubmit={
                form.loginMethod === "phone" ? handleSubmit : handleEmailSubmit
              }
              style={{ display: "flex", flexDirection: "column" }}
            >
              <div id="recaptcha-container"></div>
              {form.loginMethod === "phone" && !form.confirmationResult && (
                <FormGroup>
                  <MuiTelInput
                    defaultCountry={"GR"}
                    onlyCountries={["GR", "CY"]}
                    value={form.mobile}
                    disabled={form.confirmationResult}
                    fullWidth
                    onChange={handleOnChange}
                    size={"small"}
                    variant={"outlined"}
                    error={!isValidPhone}
                    helperText={
                      !isValidPhone ? t("error_invalid_phone_number") : ""
                    }
                    InputLabelProps={{
                      style: { color: "#7E8CA7", fontFamily: "Play" },
                    }}
                    InputProps={{
                      disableunderline: "true",
                      variant: "outlined",
                      classes: { notchedOutline: formStyles.input },
                      style: {
                        backgroundColor: "#182229",
                        color: "#FFFFFF",
                        borderRadius: "8px",
                        border: "1px solid #212D38",
                        marginBottom: "16px",
                        padding: "6px 0 4px 0",
                      },
                    }}
                  />
                </FormGroup>
              )}

              {form.loginMethod === "phone" && form.confirmationResult && (
                <FormGroup>
                  <TextField
                    autoFocus
                    fullWidth
                    name="code"
                    required
                    value={form.code}
                    size={"small"}
                    placeholder={t("insert_code")}
                    onChange={({ target }) => updateField(target)}
                    InputProps={{
                      disableunderline: "true",
                      classes: { notchedOutline: formStyles.input },
                      style: {
                        backgroundColor: "#182229",
                        color: "#FFFFFF",
                        borderRadius: "8px",
                        border: "1px solid #212D38",
                        marginBottom: "16px",
                        padding: "6px 0 4px 0",
                      },
                    }}
                  />
                </FormGroup>
              )}

              {form.loginMethod === "email" && (
                <FormGroup>
                  <TextField
                    autoFocus
                    fullWidth
                    name="email"
                    required
                    type="email"
                    value={form.email}
                    size={"small"}
                    placeholder={t("email_placeholder")}
                    onChange={({ target }) => updateField(target)}
                    error={!isValidEmail}
                    helperText={!isValidEmail ? t("invalid_email") : ""}
                    InputProps={{
                      disableunderline: "true",
                      classes: { notchedOutline: formStyles.input },
                      style: {
                        backgroundColor: "#182229",
                        color: "#FFFFFF",
                        borderRadius: "8px",
                        border: "1px solid #212D38",
                        marginBottom: "16px",
                        padding: "6px 0 4px 0",
                      },
                    }}
                  />
                </FormGroup>
              )}
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <FormGroup>
                  <FormControlLabel
                    id="rememberMe"
                    sx={{
                      color: form.loginMethod === "email" ? "#7E8CA7" : "#fff",
                      "& .MuiFormControlLabel-label.Mui-disabled": {
                        color: "#7E8CA7",
                      },
                    }}
                    control={
                      <Checkbox
                        checked={!!form.rememberMe}
                        disabled={form.loginMethod === "email"}
                        onChange={handleRememberMe}
                        checkedIcon={<BpCheckedIcon />}
                        icon={<BpIcon />}
                        name="rememberMe"
                      />
                    }
                    label={t("remember_me")}
                  />
                </FormGroup>
                <TranslationToggle />
              </Stack>
              {form.loginMethod === "email" && (
                <Box
                  sx={{ color: "#7E8CA7", fontSize: "12px", marginTop: "-4px" }}
                >
                  {t("email_login_remembered_helper")}
                </Box>
              )}

              <LoadingButton
                sx={{
                  marginTop: "20px",
                }}
                type="submit"
                loading={form.status === "pending"}
                variant="contained"
                disabled={
                  form.loginMethod === "phone"
                    ? !isValidPhone
                    : !form.email || !isValidEmail || form.status === "pending"
                }
              >
                <span className={styles.buttonText}>
                  {form.loginMethod === "phone"
                    ? form.confirmationResult
                      ? t("sign_in")
                      : t("send_code")
                    : form.status === "success"
                    ? t("login_success")
                    : form.status === "pending"
                    ? t("signing_in")
                    : t("send_magic_link")}
                </span>
              </LoadingButton>
            </form>
          </>
        )}
      </Box>
    </Box>
  );
};
export default Login;
